import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { users } from '@cwos/api-client';
import type { users as UsersNS } from '@cwos/api-client';
import { Pill, SkeletonSet } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { SettingsHeader } from '../settings/common.js';
import {
  catalogEntries,
  diffRoles,
  groupByModule,
  searchEntries,
  sensitivityBadge,
  type CatalogEntry,
} from './catalog.js';
import { LoadFailure } from '../LoadFailure.js';
import { ComposedRoles } from './ComposedRoles.js';
import { Superusers } from './Superusers.js';

/**
 * Settings → Roles & permissions — an UNDERSTANDABLE view of the real
 * authorization system, never a second source of authority.
 *
 * Everything on this screen is served by `GET /roles/overview`: the roles the
 * server knows (system + any org-custom), the active accounts holding each,
 * each role's permission keys, the live seeded catalog, and the plain-language
 * metadata written from the routes that actually enforce each key. The screen
 * renders those truths and mutates NOTHING — changing what a person can do
 * happens on their page under People & roles, through the existing grant
 * routes, exactly as before.
 *
 * Three ways in, all over the same data: the role list ("what can this role
 * do?"), a search across the whole catalog (by human name, description, or
 * internal key), and a two-role comparison. High-sensitivity keys carry their
 * warning sentence EVERYWHERE they appear — a warning that only shows on one
 * tab is a warning somebody won't see.
 */
export function RolesPermissions() {
  const navigate = useNav();
  const overview = useQuery({
    queryKey: ['roles', 'overview'],
    queryFn: () => users.rolesOverview(),
  });

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [compareRoleId, setCompareRoleId] = useState<string>('');
  const [query, setQuery] = useState('');

  const data = overview.data;
  const entries = useMemo(() => (data ? catalogEntries(data) : []), [data]);
  const entryByKey = useMemo(() => new Map(entries.map((e) => [e.key, e])), [entries]);

  if (overview.isLoading) {
    return (
      <div className="rn-col">
        <SettingsHeader title="Roles & permissions" />
        <SkeletonSet surface="table" rows={5} />
      </div>
    );
  }
  if (overview.isError || !data) {
    return (
      <div className="rn-col">
        <SettingsHeader title="Roles & permissions" />
        <LoadFailure
          error={overview.error}
          message="We couldn’t load the roles overview just now."
          onRetry={() => void overview.refetch()}
        />
      </div>
    );
  }

  const selectedRole = data.roles.find((r) => r.id === selectedRoleId) ?? null;
  const compareRole = data.roles.find((r) => r.id === compareRoleId) ?? null;
  const searching = query.trim().length > 0;
  const results = searching ? searchEntries(entries, query) : [];

  return (
    <div className="rn-col">
      <SettingsHeader
        title="Roles & permissions"
        blurb="What each role can actually do — read live from the authorization system. This screen changes nothing: it shows the same truths the server enforces on every request."
      />

      {/* The honest editing note: this screen would otherwise imply editability. */}
      <div className="rn-card" role="note">
        <p className="rn-muted" style={{ marginBottom: 0 }}>
          To change what one person can do, open their page under{' '}
          <a
            className="rn-link"
            href="/settings/people"
            onClick={(e) => {
              e.preventDefault();
              navigate('/settings/people');
            }}
          >
            People &amp; roles
          </a>{' '}
          and grant or remove roles and direct permissions there. To define a role of your own, or
          to move the superuser tier, use the two sections at the foot of this screen — both change
          who can do what, and both are checked against what you yourself hold.
        </p>
      </div>

      <div className="rn-card">
        <label htmlFor="perm-search" className="rn-card-title" style={{ display: 'block' }}>
          Search permissions
        </label>
        <p className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
          Search by what a permission is called, what it does, or its internal key.
        </p>
        <input
          id="perm-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. schedules, export, audit:read"
          style={{ width: '100%', maxWidth: '28rem' }}
        />
      </div>

      {searching ? (
        <section className="rn-card" aria-label="Search results">
          <h2 className="rn-card-title">
            {results.length === 0
              ? 'No permissions match'
              : `${results.length} matching permission${results.length === 1 ? '' : 's'}`}
          </h2>
          {results.length === 0 ? (
            <p className="rn-muted" style={{ marginBottom: 0 }}>
              Nothing in the catalog matches “{query.trim()}”. Try part of a name, a description, or
              an internal key.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {results.map((entry) => (
                <li key={entry.key} style={{ padding: 'var(--space-8) 0' }}>
                  <PermissionRow entry={entry} holders={holdersOf(data.roles, entry.key)} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <>
          <section aria-label="Roles">
            <h2 className="rn-card-title" style={{ fontSize: '1rem' }}>
              Roles
            </h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
              Member counts are active accounts — invited, suspended, and deactivated accounts are
              not counted.
            </p>
            <div className="set-grid" style={{ marginTop: 'var(--space-10)' }}>
              {data.roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  className="set-card"
                  aria-pressed={role.id === selectedRoleId}
                  onClick={() => setSelectedRoleId(role.id === selectedRoleId ? null : role.id)}
                  style={
                    role.id === selectedRoleId
                      ? { outline: '2px solid var(--focus, #4c6ef5)' }
                      : undefined
                  }
                >
                  <span className="set-card-title">{role.name}</span>
                  <span className="set-card-desc">
                    {role.memberCount} active {role.memberCount === 1 ? 'member' : 'members'} ·{' '}
                    {/*
                      A NON-SYSTEM ROLE'S KEY COUNT IS NOT A COUNT OF ITS KEYS.
                      `GET /roles/overview` builds `permissionKeys` by joining
                      `role_permission`. A role composed through
                      `POST /orgchart/roles` writes `composed_role_permission`
                      instead, which this payload never reads — so every
                      composed role arrives with an empty array and rendering
                      "0 permissions" states, falsely, that it confers nothing.
                      An org-custom role has no other way to exist in this
                      product (`insert into role` appears once, in
                      `orgchart/repository.ts`), so the empty array is the
                      absence of a JOIN and the honest word is "not readable".
                    */}
                    {role.isSystem
                      ? `${role.permissionKeys.length} ${role.permissionKeys.length === 1 ? 'permission' : 'permissions'} · System role`
                      : 'permissions not readable — composed roles have no read route'}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {selectedRole ? (
            <section className="rn-card" aria-label={`What can the ${selectedRole.name} role do?`}>
              <div className="rn-card-head">
                <h2 className="rn-card-title">What can the {selectedRole.name} role do?</h2>
                <label
                  className="rn-muted"
                  style={{ display: 'inline-flex', gap: 'var(--space-6)', alignItems: 'center' }}
                >
                  Compare with
                  <select value={compareRoleId} onChange={(e) => setCompareRoleId(e.target.value)}>
                    <option value="">No comparison</option>
                    {data.roles
                      .filter((r) => r.id !== selectedRole.id)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                  </select>
                </label>
              </div>

              {compareRole && compareRole.id !== selectedRole.id ? (
                <CompareView
                  a={selectedRole}
                  b={compareRole}
                  entryByKey={entryByKey}
                  modules={data.modules}
                />
              ) : selectedRole.permissionKeys.length === 0 ? (
                <p className="rn-muted" style={{ marginBottom: 0 }}>
                  This role carries no permissions of its own. People holding it can still hold
                  direct per-person grants.
                </p>
              ) : (
                <RolePermissionList
                  role={selectedRole}
                  entryByKey={entryByKey}
                  modules={data.modules}
                />
              )}
            </section>
          ) : (
            <p className="rn-muted">Choose a role to see what it can do.</p>
          )}
        </>
      )}

      {/*
        THE TWO WRITE SECTIONS, at the foot and after the read.
        This screen's own docblock says it "mutates NOTHING" and points every
        change at People & roles. That was true of the catalog above and is no
        longer true of the screen, so the note at the top says so. They live
        here rather than on a route of their own because `App.tsx` is
        integrator-owned and because Settings → Roles & permissions is where
        `docs/intelligence/V2-UI-COVERAGE.md` §O8 places composed roles and
        superuser designation.
      */}
      <ComposedRoles overview={data} />
      <Superusers />
    </div>
  );
}

function holdersOf(roles: UsersNS.RoleOverview[], key: string): string[] {
  return roles.filter((r) => r.permissionKeys.includes(key)).map((r) => r.name);
}

/** A role's permissions, grouped by module, with the human layer on each. */
function RolePermissionList({
  role,
  entryByKey,
  modules,
}: {
  role: UsersNS.RoleOverview;
  entryByKey: Map<string, CatalogEntry>;
  modules: string[];
}) {
  const entries = role.permissionKeys.map(
    (key) =>
      entryByKey.get(key) ?? {
        key,
        meta: undefined,
        seededDescription: '',
      },
  );
  const groups = groupByModule(entries, modules);
  return (
    <div className="rn-col" style={{ gap: 'var(--space-12)' }}>
      {groups.map((group) => (
        <div key={group.module}>
          <h3 className="rn-card-title" style={{ fontSize: '0.9rem' }}>
            {group.module}
          </h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {group.entries.map((entry) => (
              <li key={entry.key} style={{ padding: 'var(--space-8) 0' }}>
                <PermissionRow entry={entry} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * WHAT THE ACCESS PILL SAYS. A map rather than a ternary, because the ternary
 * it replaces printed "Write" for anything that was not exactly 'read' — so
 * when PH-AUTHZ-003 made `service:manage` the door on a READ of every named
 * client's payer and ceiling, the screen went on describing it as write-only
 * and an administrator composing a role was reading a false safety argument.
 * Unknown values still fall back to "Write", the cautious end.
 */
const ACCESS_LABEL: Partial<Record<UsersNS.PermissionAccess, string>> = {
  read: 'Read',
  write: 'Write',
  read_write: 'Read & write',
};

/**
 * One permission, in human terms. The internal key renders ONLY inside the
 * advanced disclosure; high/elevated keys carry their badge, and a high key's
 * warning sentence renders wherever the permission is shown.
 */
function PermissionRow({ entry, holders }: { entry: CatalogEntry; holders?: string[] }) {
  const meta = entry.meta;
  const badge = meta ? sensitivityBadge(meta.sensitivity) : null;
  return (
    <div>
      <div
        style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center', flexWrap: 'wrap' }}
      >
        <span style={{ fontWeight: 600 }}>{meta ? meta.name : entry.key}</span>
        {meta ? <Pill variant="plain">{ACCESS_LABEL[meta.access] ?? 'Write'}</Pill> : null}
        {badge ? <Pill variant={badge.variant}>{badge.label}</Pill> : null}
      </div>
      <p className="rn-muted" style={{ marginTop: 'var(--space-4)', marginBottom: 0 }}>
        {meta ? meta.description : entry.seededDescription}
      </p>
      {meta?.warning ? (
        <p role="note" style={{ marginTop: 'var(--space-4)', marginBottom: 0, fontWeight: 500 }}>
          ⚠ {meta.warning}
        </p>
      ) : null}
      {meta ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-4)', marginBottom: 0 }}>
          Scope: {meta.scope}
        </p>
      ) : null}
      {meta?.dependsOn ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-4)', marginBottom: 0 }}>
          Works with: {meta.dependsOn}
        </p>
      ) : null}
      {holders && holders.length > 0 ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-4)', marginBottom: 0 }}>
          Held by: {holders.join(', ')}
        </p>
      ) : null}
      <details style={{ marginTop: 'var(--space-4)' }}>
        <summary className="rn-muted" style={{ cursor: 'pointer' }}>
          Advanced details
        </summary>
        <p className="rn-muted" style={{ marginBottom: 0 }}>
          Internal key: <code>{entry.key}</code>
          {meta ? null : (
            <>
              {' '}
              — this key has no descriptive metadata yet; the text above is its seeded catalog
              description.
            </>
          )}
        </p>
      </details>
    </div>
  );
}

/**
 * Side-by-side capability diff. A permission held by only one of the two roles
 * appears under that role's column; what both hold is summarized per module
 * below, so the differences stay the loudest thing on the screen.
 */
function CompareView({
  a,
  b,
  entryByKey,
  modules,
}: {
  a: UsersNS.RoleOverview;
  b: UsersNS.RoleOverview;
  entryByKey: Map<string, CatalogEntry>;
  modules: string[];
}) {
  const diff = diffRoles(a.permissionKeys, b.permissionKeys);
  const toEntries = (keys: string[]) =>
    keys.map((key) => entryByKey.get(key) ?? { key, meta: undefined, seededDescription: '' });
  return (
    <div>
      <p className="rn-muted">
        {diff.shared.length} permission{diff.shared.length === 1 ? '' : 's'} held by both roles.
      </p>
      <div className="rn-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-12)' }}>
        <section aria-label={`Only ${a.name}`}>
          <h3 className="rn-card-title" style={{ fontSize: '0.9rem' }}>
            Only {a.name}
          </h3>
          <DiffColumn entries={toEntries(diff.onlyA)} modules={modules} otherName={b.name} />
        </section>
        <section aria-label={`Only ${b.name}`}>
          <h3 className="rn-card-title" style={{ fontSize: '0.9rem' }}>
            Only {b.name}
          </h3>
          <DiffColumn entries={toEntries(diff.onlyB)} modules={modules} otherName={a.name} />
        </section>
      </div>
    </div>
  );
}

function DiffColumn({
  entries,
  modules,
  otherName,
}: {
  entries: CatalogEntry[];
  modules: string[];
  otherName: string;
}) {
  if (entries.length === 0) {
    return (
      <p className="rn-muted" style={{ marginBottom: 0 }}>
        Nothing — every permission this role holds, {otherName} holds too.
      </p>
    );
  }
  const groups = groupByModule(entries, modules);
  return (
    <div className="rn-col" style={{ gap: 'var(--space-10)' }}>
      {groups.map((group) => (
        <div key={group.module}>
          <h4
            className="rn-muted"
            style={{ margin: 0, fontSize: '0.8rem', textTransform: 'uppercase' }}
          >
            {group.module}
          </h4>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {group.entries.map((entry) => {
              const badge = entry.meta ? sensitivityBadge(entry.meta.sensitivity) : null;
              return (
                <li key={entry.key} style={{ padding: 'var(--space-6) 0' }}>
                  <span>{entry.meta ? entry.meta.name : entry.key}</span>{' '}
                  {badge ? <Pill variant={badge.variant}>{badge.label}</Pill> : null}
                  {entry.meta?.warning ? (
                    <p role="note" className="rn-muted" style={{ margin: 0 }}>
                      ⚠ {entry.meta.warning}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
